import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NotificationTargetSelector } from "./NotificationTargetSelector";

// Notification types with human-readable labels
const NOTIFICATION_TYPES = [
  { value: "announcement", label: "Announcement" },
  { value: "reminder", label: "Reminder" },
  { value: "alert", label: "Alert" },
  { value: "payment", label: "Payment" },
  { value: "registration", label: "Registration" },
  { value: "attendance", label: "Attendance" },
  { value: "message", label: "Message" },
];

// Schema for notification form
const notificationFormSchema = z.object({
  content: z.string()
    .min(5, { message: "Content must be at least 5 characters" })
    .max(500, { message: "Content cannot exceed 500 characters" }),
  type: z.string({
    required_error: "Please select a notification type",
  }),
  // Optional title field
  title: z.string().optional(),
  // Optional link field
  link: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal("")),
});

type NotificationFormValues = z.infer<typeof notificationFormSchema>;

interface NotificationFormProps {
  organizationId: string;
  onSuccess?: () => void;
}

export const NotificationForm: React.FC<NotificationFormProps> = ({ 
  organizationId,
  onSuccess
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetType, setTargetType] = useState<string>("all");
  const [targetId, setTargetId] = useState<string>("");
  
  const form = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationFormSchema),
    defaultValues: {
      content: "",
      type: "announcement",
      title: "",
      link: "",
    },
  });

  const handleSubmit = async (values: NotificationFormValues) => {
    if (!user) return;
    
    setIsSubmitting(true);
    
    try {
      // Insert notification with target information included directly in the notification
      // NOTE: We're using the metadata field to store targeting info instead of a separate 
      // notification_targets table, which is why there are linter errors referencing that table.
      // This approach avoids having to create and manage a separate table.
      const { data: notification, error: notificationError } = await supabase
        .from("notifications")
        .insert({
          content: values.content,
          type: values.type,
          created_by: user.id,
          status: "sent",
          ...(values.title && { title: values.title }),
          ...(values.link && { link: values.link }),
          ...(organizationId && { organization_id: organizationId }),
          // Store target information directly in the notification
          metadata: JSON.stringify({
            target_type: targetType,
            target_id: targetId || null
          })
        })
        .select()
        .single();
        
      if (notificationError) throw notificationError;
      
      // Success handling
      toast.success("Notification sent successfully!");
      form.reset();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Send Notification</CardTitle>
        <CardDescription>
          Create a notification to send to your users
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notification Type</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {NOTIFICATION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Brief title for the notification" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter notification content" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://example.com/page" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="space-y-4">
              <FormLabel>Recipients</FormLabel>
              <NotificationTargetSelector
                organizationId={organizationId}
                targetType={targetType}
                setTargetType={setTargetType}
                targetId={targetId}
                setTargetId={setTargetId}
              />
            </div>
            
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Notification"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}; 